import _flowRight from 'lodash/flowRight';
import { isBefore } from 'date-fns';
import { normalize } from 'normalizr';
import http from '@homeservices/api-kit/axios';
import {
  getAppointment,
  updateAppointment,
  createAppointment,
  deleteAppointment,
} from '@homeservices/api-kit/workorder/appointments';
import { /* JOBS_WITH_APPOINTMENTS_SCHEMA, */ APPOINTMENTS_SCHEMA, PRO_WORK_ORDER_MATCHES_SCHEMA } from '@/store/modules/entities';
import {
  withRequestLifecycle,
  REQUEST_LIFECYCLE,
} from '@/utils/request-lifecycle';
import addUTCTimeZoneDesignator from '@/utils/add-utc-time-zone-designator';

const MUTATION_KEY = 'patchAppointments';

const emptyArray = [];

// const extractAppointmentsFromJob = (jobId, entities) => {
//   const [matchId] = entities.jobs[jobId].matches;

//   return entities.matches[matchId].appointments;
// };
// const reconcileAppointmentsWithinJob = (
//   jobId,
//   skillsProfile,
//   entities,
//   appointments,
//   strategy
// ) => {
//   const oldAppointments = extractAppointmentsFromJob(jobId, entities) || emptyArray;

//   return {
//     id: jobId,
//     matches: [
//       {
//         skills_profile: skillsProfile,
//         appointments: strategy(oldAppointments, appointments),
//       },
//     ],
//   };
// };
// const updateStrategy = (oldEntities, newEntitiesToAdd) => {
//   const result = [...oldEntities];

//   newEntitiesToAdd.forEach((entity) => {
//     const index = result.indexOf(entity.id);

//     if (index !== -1) {
//       result[index] = entity;
//     } else {
//       result.push(entity);
//     }
//   });

//   return result;
// };
const removeStrategy = (oldEntities, entitiesToRemove) =>
  oldEntities.filter(entity => !entitiesToRemove.includes(entity));

export default {
  state: {
    current: {
      status: REQUEST_LIFECYCLE.UNINITIALIZED,
      error: null,
      result: emptyArray,
      count: 0,
      total: 0,
      hasMore: false,
    },
  },
  getters: {
    currentJobAppointmentsMeta: (state) => {
      const { result, ...meta } = state.current;

      return meta;
    },
    currentRawJobAppointments: state => state.current.result,
    currentSortedRawJobAppointments: (
      state,
      _,
      __,
      { 'entities/appointments': rawAppointments }
    ) =>
      [...state.current.result]
        .map(appointmentId => rawAppointments[appointmentId] && appointmentId)
        .filter(Boolean)
        .sort(
          (first, second) =>
            new Date(rawAppointments[first].start_dtm)
            - new Date(rawAppointments[second].start_dtm)
        ),
    sortedByUpcomingDateJobAppointments: (
      _,
      { currentSortedRawJobAppointments },
      __,
      { 'entities/appointments': rawAppointments }
    ) => {
      const past = [];
      const upcoming = [];

      currentSortedRawJobAppointments.forEach((appointmentId) => {
        if (
          isBefore(
            new Date(addUTCTimeZoneDesignator(new Date(rawAppointments[appointmentId].end_dtm))),
            Date.now()
          )
        ) {
          past.push(appointmentId);
        } else {
          upcoming.push(appointmentId);
        }
      });

      return [...upcoming, ...past];
    },
  },
  actions: {
    fetchAppointments: _flowRight(
      withRequestLifecycle(MUTATION_KEY),
      () => async (
        context,
        { injected: { performFulfillment }, params = {} }
      ) => {
        const prm = {
          order_hash: params.order_hash,
          service_provider_id: params.service_provider_id,
        };
        const {
          data: { results, total },
        } = await getAppointment(prm);

        const resultsWithId = results.map(r => ({ ...r, id: r.appointment_id }));

        const { entities, result } = normalize(
          resultsWithId,
          [APPOINTMENTS_SCHEMA]
        );

        return performFulfillment({
          entities,
          result,
          total,
        });
      }
    )(),
    createOrUpdateAppointments: _flowRight(
      withRequestLifecycle(MUTATION_KEY),
      () => async (
        {
          rootState: { entities: oldEntities },
          getters: { currentJobAppointmentsMeta },
        },
        {
          injected: { performFulfillment },
          appointmentId,
          params = {},
        }
      ) => {
        const appointmentTimezone = global.Intl.DateTimeFormat().resolvedOptions()
          .timeZone;
        const prm = {
          time_zone: appointmentTimezone,
          ...params,
        };
        const isNew = !!appointmentId;
        const createOrUpdate = isNew
          ? updateAppointment.bind(undefined, appointmentId)
          : createAppointment;
        const { data } = await createOrUpdate(prm);
        let { total } = currentJobAppointmentsMeta;

        if (isNew) {
          total += 1;
        }

        const dataWithId = {
          ...data,
          id: data.appointment_id,
        };

        const { matches: matchId } = oldEntities.proWorkOrders[prm.order_hash];
        const { appointments } = oldEntities.proWorkOrdersMatches[matchId];

        const app = [dataWithId];
        if (appointments && appointments.length) {
          app.push(...appointments);
        }

        const { entities } = normalize(
          {
            id: matchId,
            appointments: app,
          },
          PRO_WORK_ORDER_MATCHES_SCHEMA
        );

        performFulfillment({
          entities,
          result: [dataWithId.id],
          total,
        });

        return dataWithId;
      }
    )(),
    deleteAppointments: _flowRight(
      withRequestLifecycle(MUTATION_KEY),
      () => async (
        {
          rootState: { entities: oldEntities },
        },
        {
          injected: { performFulfillment },
          order_hash,
          appointmentId,
        }
      ) => {
        await deleteAppointment(appointmentId);

        const { matches: matchId } = oldEntities.proWorkOrders[order_hash];
        const { appointments } = oldEntities.proWorkOrdersMatches[matchId];

        const oldAppointments = appointments;
        const currentAppointment = removeStrategy(oldAppointments, [appointmentId]);

        const { entities } = normalize(
          {
            id: matchId,
            appointments: currentAppointment,
          },
          PRO_WORK_ORDER_MATCHES_SCHEMA
        );

        return performFulfillment({
          entities,
          result: currentAppointment,
          total: appointments.length,
        });
      }
    )(),
    exportAppointments: (context, { appointmentId, serviceProviderId }) => {
      return http.get(`/appointments/${appointmentId}/${serviceProviderId}/export`);
    },
  },
  mutations: {
    [MUTATION_KEY] (state, payload) {
      const { result, total } = payload;
      let {
        count = state.current.count,
        hasMore = state.current.hasMore,
      } = payload;

      if (result) {
        count = result.length;

        if (typeof total !== 'undefined') {
          hasMore = result.length < total;
        }
      }

      state.current = {
        ...state.current,
        ...payload,
        count,
        hasMore,
      };
    },
  },
};
